/**
 * Canonical Verification Navigation Helper
 * 
 * Single source of truth for all verification routing.
 * Use these helpers instead of hardcoding verify routes.
 * 
 * USAGE:
 *   import { goVerify, buildVerifyUrl } from '../utils/verificationNav';
 *   
 *   // Option 1: Navigate directly
 *   goVerify(navigate, location, null, { source: 'dashboard', isLoggedIn: true });
 *   
 *   // Option 2: Get URL for href
 *   const url = buildVerifyUrl(location.pathname);
 * 
 * SECURITY:
 *   - Only internal paths (starting with /) are allowed as return URLs
 *   - External URLs are rejected and fallback to /dashboard
 *   - undefined/empty values fallback to /dashboard
 */

import { track } from "./analytics";

const DEFAULT_RETURN = '/dashboard';

/**
 * Validate that a return URL is safe (internal path only)
 * @param {string} url - URL to validate
 * @returns {boolean} - true if safe internal path
 */
function isValidReturnUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  // Must start with / (internal path)
  if (!url.startsWith('/')) return false;
  
  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith('//')) return false;
  
  // Block javascript: and data: schemes that might slip through
  const lower = url.toLowerCase();
  if (lower.includes('javascript:') || lower.includes('data:')) return false;
  
  return true;
}

/**
 * Safely get the return URL from current location
 * @param {Location} location - React Router location object
 * @returns {string} - Current path + search params (validated)
 */
export function getReturnTo(location) {
  if (!location) return DEFAULT_RETURN;
  
  const path = location.pathname || '';
  const search = location.search || '';
  
  // Don't return to verify page itself
  if (path.includes('/verify')) {
    return DEFAULT_RETURN;
  }
  
  const returnUrl = `${path}${search}`;
  
  // Validate the URL
  if (!isValidReturnUrl(returnUrl)) {
    return DEFAULT_RETURN;
  }
  
  return returnUrl;
}

/**
 * Build the verification URL with return parameter
 * @param {string} returnTo - URL to return to after verification
 * @returns {string} - Full verification URL with encoded return param
 */
export function buildVerifyUrl(returnTo) {
  // Validate and sanitize returnTo
  const safeReturnTo = isValidReturnUrl(returnTo) ? returnTo : DEFAULT_RETURN;
  return `/verify-age?return=${encodeURIComponent(safeReturnTo)}`;
}

/**
 * Navigate to verification page with return URL
 * Emits verify_cta_clicked analytics event (non-blocking).
 * 
 * @param {Function} navigate - React Router navigate function
 * @param {Location} location - React Router location object
 * @param {string} overrideReturnTo - Optional override for return URL
 * @param {Object} opts - Options: { source?: string, isLoggedIn?: boolean }
 */
export function goVerify(navigate, location, overrideReturnTo, opts = {}) {
  const returnTo = overrideReturnTo && isValidReturnUrl(overrideReturnTo) 
    ? overrideReturnTo 
    : getReturnTo(location);
  const url = buildVerifyUrl(returnTo);
  
  // Analytics: Track verification CTA clicks (non-blocking, never prevents navigation)
  try {
    const source = opts?.source || location?.pathname?.split('/')[1] || 'unknown';
    const isLoggedIn = typeof opts?.isLoggedIn === 'boolean' ? opts.isLoggedIn : null;
    
    track("verify_cta_clicked", {
      source,
      returnTo,
      isLoggedIn,
    });
    
    // Also fire gtag if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'verify_cta_clicked', {
        source,
        return_to: returnTo,
        is_logged_in: isLoggedIn,
      });
    }
  } catch (err) {
    // Analytics should never break navigation
    if (process.env.NODE_ENV !== "production") console.error("[verificationNav] analytics error:", err);
  }
  
  navigate(url);
}

/**
 * Get the return URL from verification page query params
 * SECURITY: Only allows internal paths starting with /
 * @param {URLSearchParams} searchParams - URL search params
 * @returns {string} - Decoded return URL or default
 */
export function getVerifyReturnUrl(searchParams) {
  const returnParam = searchParams?.get('return');
  
  if (returnParam) {
    try {
      const decoded = decodeURIComponent(returnParam);
      
      // SECURITY: Only allow internal paths
      if (isValidReturnUrl(decoded)) {
        return decoded;
      } else {
        console.warn('[verificationNav] Rejected external/invalid return URL:', decoded);
      }
    } catch (e) {
      console.warn('[verificationNav] Failed to decode return URL:', e);
    }
  }
  
  return DEFAULT_RETURN;
}

/**
 * Check if current URL is a verification redirect
 * @param {Location} location - React Router location object
 * @returns {boolean}
 */
export function isVerificationRedirect(location) {
  return location?.pathname?.includes('/verify');
}

export default {
  getReturnTo,
  buildVerifyUrl,
  goVerify,
  getVerifyReturnUrl,
  isVerificationRedirect,
};
